/**
 * ChiefOfStaff Orchestrator API Client
 * Provides a clean interface for interacting with the orchestrator
 */

import { supabase } from './supabase';
import type { OrchestratorResponse, UserRequest } from '../orchestrator/types';

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: Date;
  metadata?: {
    agentsInvoked?: string[];
    toolsUsed?: string[];
    confidence?: number;
    executionTime?: number;
  };
}

export class OrchestratorAPI {
  private sessionId: string;

  constructor() {
    this.sessionId = this.generateSessionId();
  }

  /**
   * Send a message to the ChiefOfStaff Orchestrator
   */
  async sendMessage(message: string, context?: any): Promise<{
    response: OrchestratorResponse;
    chatMessage: ChatMessage;
  }> {
    try {
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        throw new Error('User not authenticated');
      }

      const request: UserRequest = {
        message,
        userId: user.id,
        context,
        sessionId: this.sessionId,
        timestamp: new Date(),
      };

      // In production, this would call a Supabase Edge Function:
      // const { data, error } = await supabase.functions.invoke('orchestrator', {
      //   body: request,
      // });

      // For now, use the orchestrator directly (client-side)
      const { ChiefOfStaffOrchestrator } = await import('../orchestrator/ChiefOfStaffOrchestrator');
      const { JobMatchAgent } = await import('../orchestrator/agents/JobMatchAgent');
      const { ResumeDraftAgent } = await import('../orchestrator/agents/ResumeDraftAgent');
      const { InterviewPrepAgent } = await import('../orchestrator/agents/InterviewPrepAgent');
      const { InterviewDebriefAgent } = await import('../orchestrator/agents/InterviewDebriefAgent');
      const { SchedulingAgent } = await import('../orchestrator/agents/SchedulingAgent');
      const { EmployerAgent } = await import('../orchestrator/agents/EmployerAgent');
      const { PrivacyAgent } = await import('../orchestrator/agents/PrivacyAgent');

      // Import tools
      const { ZoomAdapter } = await import('../orchestrator/tools/ZoomAdapter');
      const { OutlookAdapter } = await import('../orchestrator/tools/OutlookAdapter');
      const { GoogleCalendarAdapter } = await import('../orchestrator/tools/GoogleCalendarAdapter');
      const { MondayAdapter } = await import('../orchestrator/tools/MondayAdapter');
      const { SlackAdapter } = await import('../orchestrator/tools/SlackAdapter');

      const orchestrator = new ChiefOfStaffOrchestrator();

      // Register all agents
      orchestrator.registerAgent(new JobMatchAgent());
      orchestrator.registerAgent(new ResumeDraftAgent());
      orchestrator.registerAgent(new InterviewPrepAgent());
      orchestrator.registerAgent(new InterviewDebriefAgent());
      orchestrator.registerAgent(new SchedulingAgent());
      orchestrator.registerAgent(new EmployerAgent());
      orchestrator.registerAgent(new PrivacyAgent());

      // Register all tools
      orchestrator.registerTool(new ZoomAdapter());
      orchestrator.registerTool(new OutlookAdapter());
      orchestrator.registerTool(new GoogleCalendarAdapter());
      orchestrator.registerTool(new MondayAdapter());
      orchestrator.registerTool(new SlackAdapter());

      const response = await orchestrator.handleRequest(request);

      // Create chat message for display
      const chatMessage: ChatMessage = {
        id: this.generateMessageId(),
        role: 'assistant',
        content: response.response,
        timestamp: new Date(),
        metadata: {
          agentsInvoked: response.agentsInvoked,
          toolsUsed: response.toolsUsed,
          confidence: response.metadata.confidence,
          executionTime: response.metadata.executionTime,
        },
      };

      // Store in conversation history (optional)
      await this.storeMessage(user.id, message, response);

      return { response, chatMessage };

    } catch (error) {
      console.error('[OrchestratorAPI] Error sending message:', error);
      throw error;
    }
  }

  /**
   * Get conversation history
   */
  async getConversationHistory(limit: number = 50): Promise<ChatMessage[]> {
    try {
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        return [];
      }

      const { data, error } = await supabase
        .from('orchestrator_conversations')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) throw error;

      const messages: ChatMessage[] = [];

      (data || []).forEach((conv: any) => {
        messages.push({
          id: `user-${conv.id}`,
          role: 'user',
          content: conv.user_message,
          timestamp: new Date(conv.created_at),
        });
        messages.push({
          id: `assistant-${conv.id}`,
          role: 'assistant',
          content: conv.assistant_response,
          timestamp: new Date(conv.created_at),
          metadata: conv.metadata,
        });
      });

      return messages.reverse();

    } catch (error) {
      console.error('[OrchestratorAPI] Error fetching history:', error);
      return [];
    }
  }

  /**
   * Clear conversation history
   */
  async clearHistory(): Promise<void> {
    try {
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) return;

      await supabase
        .from('orchestrator_conversations')
        .delete()
        .eq('user_id', user.id);

      this.sessionId = this.generateSessionId();

    } catch (error) {
      console.error('[OrchestratorAPI] Error clearing history:', error);
    }
  }

  /**
   * Get quick action suggestions
   */
  getQuickActions(): Array<{ label: string; message: string; icon: string }> {
    return [
      {
        label: 'Find Jobs',
        message: 'Find me software engineering jobs',
        icon: '🔍',
      },
      {
        label: 'Create Resume',
        message: 'Generate a professional resume from my profile',
        icon: '📄',
      },
      {
        label: 'Interview Prep',
        message: 'Help me prepare for my upcoming interview',
        icon: '🎯',
      },
      {
        label: 'Schedule Meeting',
        message: 'Schedule a meeting with hiring manager',
        icon: '📅',
      },
      {
        label: 'Privacy Settings',
        message: 'Show me my privacy settings',
        icon: '🔒',
      },
    ];
  }

  /**
   * Store conversation in database
   */
  private async storeMessage(
    userId: string,
    userMessage: string,
    response: OrchestratorResponse
  ): Promise<void> {
    try {
      await supabase.from('orchestrator_conversations').insert({
        user_id: userId,
        session_id: this.sessionId,
        user_message: userMessage,
        assistant_response: response.response,
        metadata: {
          agentsInvoked: response.agentsInvoked,
          toolsUsed: response.toolsUsed,
          confidence: response.metadata.confidence,
          executionTime: response.metadata.executionTime,
          nextSteps: response.nextSteps,
        },
        created_at: new Date().toISOString(),
      });
    } catch (error) {
      console.error('[OrchestratorAPI] Error storing message:', error);
      // Don't throw - conversation storage is not critical
    }
  }

  /**
   * Generate unique session ID
   */
  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Generate unique message ID
   */
  private generateMessageId(): string {
    return `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
}

// Export singleton instance
export const orchestratorAPI = new OrchestratorAPI();
